<script setup>
import { ref, computed, watch, toRaw } from 'vue';
import ConditionBuilder from './ConditionBuilder.vue';
import TemplatePickersToolbar from '../TemplatePickers/TemplatePickersToolbar.vue';
import NotifyWhatsappConfig from './actions/NotifyWhatsappConfig.vue';

const props = defineProps({
  automation: { type: Object, default: null },
  apiSample: { type: [Object, Array, String, Number, Boolean], default: null },
  allSlots: { type: Array, default: () => [] },
  agentBotId: { type: Number, default: null },
  excludeSlotId: { type: [Number, String], default: null },
});

const emit = defineEmits(['save', 'cancel', 'test']);

// Safe deep clone that:
// - Unwraps Vue reactive proxies via toRaw (avoids Proxy recursion edge cases)
// - Handles circular references via WeakMap (avoids "Maximum call stack size exceeded")
// - Skips non-JSON-safe values (functions, DOM nodes, etc.)
// - Uses explicit stack (iterative) to be immune to call-stack-size limits
function safeClone(value) {
  const seen = new WeakMap();

  function plainValue(v) {
    if (v === null || v === undefined) return v;
    const t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean') return v;
    if (t === 'function' || t === 'symbol') return undefined;
    if (t !== 'object') return undefined;
    if (v instanceof Date) return v.toISOString();
    if (typeof Element !== 'undefined' && v instanceof Element) return undefined;
    if (typeof Event !== 'undefined' && v instanceof Event) return undefined;
    return v;
  }

  function unwrap(v) {
    const plain = plainValue(v);
    if (plain !== v) return plain;
    return toRaw(v);
  }

  const rootRaw = unwrap(value);
  if (rootRaw === null || rootRaw === undefined) return rootRaw;
  if (typeof rootRaw !== 'object') return rootRaw;

  const root = Array.isArray(rootRaw) ? [] : {};
  seen.set(rootRaw, root);

  const stack = [{ src: rootRaw, dst: root }];
  while (stack.length) {
    const { src, dst } = stack.pop();
    const keys = Array.isArray(src) ? src.map((_, i) => i) : Object.keys(src);
    keys.forEach(key => {
      const child = unwrap(src[key]);
      if (child === undefined) return;
      if (child === null || typeof child !== 'object') {
        dst[key] = child;
        return;
      }
      if (seen.has(child)) {
        dst[key] = seen.get(child);
        return;
      }
      const container = Array.isArray(child) ? [] : {};
      seen.set(child, container);
      dst[key] = container;
      stack.push({ src: child, dst: container });
    });
  }
  return root;
}

function serializeForm(value) {
  const conditions = safeClone(value?.conditions);
  const actionConfig = safeClone(value?.action_config);
  return {
    name: (value?.name || '').toString(),
    action_type: value?.action_type,
    enabled: value?.enabled !== false,
    conditions: conditions && typeof conditions === 'object' ? conditions : { combinator: 'all', rules: [] },
    action_config: actionConfig && typeof actionConfig === 'object' ? actionConfig : {},
  };
}

const ACTION_TYPES = [
  { value: 'go_to_slot', label: 'Ir para slot', icon: 'i-lucide-corner-down-right' },
  { value: 'send_message', label: 'Enviar mensagem', icon: 'i-lucide-send' },
  { value: 'notify_whatsapp', label: 'Notificar WhatsApp', icon: 'i-lucide-phone' },
  { value: 'change_status', label: 'Alterar status', icon: 'i-lucide-refresh-ccw' },
  { value: 'add_label', label: 'Adicionar etiqueta', icon: 'i-lucide-tag' },
  { value: 'assign_agent', label: 'Atribuir agente', icon: 'i-lucide-user-check' },
  { value: 'extract_fields', label: 'Extrair fields', icon: 'i-lucide-download' },
];

const STATUS_OPTIONS = ['open', 'pending', 'resolved', 'snoozed', 'ai_pause'];

const defaultAutomation = () => ({
  name: '',
  action_type: 'go_to_slot',
  enabled: true,
  conditions: { combinator: 'all', rules: [] },
  action_config: {},
});

const form = ref(defaultAutomation());

watch(
  () => props.automation,
  next => {
    if (!next) {
      form.value = defaultAutomation();
      return;
    }
    const cloned = safeClone(next) || {};
    form.value = {
      ...defaultAutomation(),
      ...cloned,
      conditions: cloned.conditions && Object.keys(cloned.conditions).length
        ? cloned.conditions
        : { combinator: 'all', rules: [] },
      action_config: cloned.action_config || {},
    };
  },
  { immediate: true }
);

const onTypeChange = () => {
  form.value.action_config = {};
};

const mappings = computed(() => form.value.action_config.mappings || []);
const addMapping = () => {
  form.value.action_config = {
    ...form.value.action_config,
    mappings: [...mappings.value, { api_path: '', field_name: '' }],
  };
};
const updateMapping = (idx, partial) => {
  const list = [...mappings.value];
  list[idx] = { ...list[idx], ...partial };
  form.value.action_config = { ...form.value.action_config, mappings: list };
};
const removeMapping = idx => {
  const list = [...mappings.value];
  list.splice(idx, 1);
  form.value.action_config = { ...form.value.action_config, mappings: list };
};

const appendToField = (key, payload) => {
  const current = form.value.action_config[key] || '';
  form.value.action_config = {
    ...form.value.action_config,
    [key]: `${current}${payload.token}`,
  };
};

const submit = () => {
  try {
    emit('save', serializeForm(form.value));
  } catch (err) {
    console.error('[AutomationForm.submit] failed to serialize form', err);
  }
};

const cancel = () => emit('cancel');

const test = () => {
  try {
    emit('test', serializeForm(form.value));
  } catch (err) {
    console.error('[AutomationForm.test] failed to serialize form', err);
  }
};
</script>

<template>
  <div class="space-y-4 p-3 border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800">
    <div class="flex flex-wrap items-center gap-3">
      <div class="flex-1 min-w-[220px]">
        <label class="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
          {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NAME', 'Nome da automação') }}
        </label>
        <input
          v-model="form.name"
          type="text"
          class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700"
          placeholder="Ex: Se status=ok, ir para checkout"
        />
      </div>
      <label class="inline-flex items-center gap-2 text-xs mt-5 text-slate-700 dark:text-slate-200">
        <input
          v-model="form.enabled"
          type="checkbox"
          class="rounded border-slate-300 text-woot-500 focus:ring-woot-500"
        />
        {{ $t('OMNI.SLOTS.API_AUTOMATIONS.ENABLED', 'Ativada') }}
      </label>
    </div>

    <div>
      <div class="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
        {{ $t('OMNI.SLOTS.API_AUTOMATIONS.CONDITIONS', 'Quando') }}
      </div>
      <ConditionBuilder
        v-model="form.conditions"
        :api-sample="apiSample"
        :all-slots="allSlots"
        :agent-bot-id="agentBotId"
        :exclude-slot-id="excludeSlotId"
      />
    </div>

    <div>
      <div class="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
        {{ $t('OMNI.SLOTS.API_AUTOMATIONS.ACTION', 'Então') }}
      </div>
      <div class="rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3 space-y-3">
        <div>
          <label class="block text-xs text-slate-500 mb-1">Tipo de ação</label>
          <select
            v-model="form.action_type"
            class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700"
            @change="onTypeChange"
          >
            <option
              v-for="a in ACTION_TYPES"
              :key="a.value"
              :value="a.value"
            >
              {{ a.label }}
            </option>
          </select>
        </div>

        <!-- go_to_slot -->
        <div v-if="form.action_type === 'go_to_slot'">
          <label class="block text-xs text-slate-500 mb-1">Slot de destino</label>
          <select
            v-model="form.action_config.target_slot_config_id"
            class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700"
          >
            <option :value="null">-- selecione --</option>
            <option
              v-for="s in allSlots"
              :key="s.id"
              :value="s.id"
            >
              {{ s.slot_name }} (priority: {{ s.priority }})
            </option>
          </select>
        </div>

        <!-- send_message -->
        <div v-if="form.action_type === 'send_message'">
          <div class="flex items-center justify-between mb-1">
            <label class="block text-xs text-slate-500">Mensagem</label>
            <TemplatePickersToolbar
              :agent-bot-id="agentBotId"
              :exclude-slot-id="excludeSlotId"
              :all-slots="allSlots"
              :api-sample="apiSample"
              @pick="appendToField('message', $event)"
            />
          </div>
          <textarea
            v-model="form.action_config.message"
            rows="3"
            class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 font-mono"
            placeholder="Olá {{contact.name}}! Seu pedido {{field.pedido_id}} foi confirmado."
          />
          <label class="flex items-center gap-2 mt-2 cursor-pointer select-none w-fit">
            <input
              v-model="form.action_config.private"
              type="checkbox"
              class="rounded border-slate-400 text-woot-500 focus:ring-woot-500"
            />
            <span class="text-xs text-slate-600 dark:text-slate-300">Mensagem privada (nota interna)</span>
          </label>
        </div>

        <!-- notify_whatsapp -->
        <NotifyWhatsappConfig
          v-if="form.action_type === 'notify_whatsapp'"
          v-model="form.action_config"
          :agent-bot-id="agentBotId"
          :exclude-slot-id="excludeSlotId"
          :all-slots="allSlots"
          :api-sample="apiSample"
        />

        <!-- change_status -->
        <div v-if="form.action_type === 'change_status'">
          <label class="block text-xs text-slate-500 mb-1">Novo status</label>
          <select
            v-model="form.action_config.status"
            class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700"
          >
            <option :value="null">-- selecione --</option>
            <option
              v-for="s in STATUS_OPTIONS"
              :key="s"
              :value="s"
            >
              {{ s }}
            </option>
          </select>
        </div>

        <!-- add_label -->
        <div v-if="form.action_type === 'add_label'">
          <div class="flex items-center justify-between mb-1">
            <label class="block text-xs text-slate-500">Nome da etiqueta</label>
            <TemplatePickersToolbar
              :agent-bot-id="agentBotId"
              :exclude-slot-id="excludeSlotId"
              :all-slots="allSlots"
              :api-sample="apiSample"
              @pick="appendToField('label_name', $event)"
            />
          </div>
          <input
            v-model="form.action_config.label_name"
            type="text"
            class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 font-mono"
            placeholder="pedido-urgente"
          />
        </div>

        <!-- assign_agent -->
        <div v-if="form.action_type === 'assign_agent'">
          <label class="inline-flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200 mb-2">
            <input
              v-model="form.action_config.assign_first_available"
              type="checkbox"
            />
            Primeiro disponível
          </label>
          <div v-if="!form.action_config.assign_first_available">
            <label class="block text-xs text-slate-500 mb-1">ID do agente</label>
            <input
              v-model.number="form.action_config.agent_id"
              type="number"
              class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700"
            />
          </div>
        </div>

        <!-- extract_fields -->
        <div v-if="form.action_type === 'extract_fields'">
          <div class="text-xs text-slate-500 mb-2">
            Mapeie caminhos da resposta da API para fields da conversa.
          </div>
          <div class="space-y-2">
            <div
              v-for="(mapping, idx) in mappings"
              :key="idx"
              class="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700/40 p-2 space-y-2"
            >
              <div class="flex items-center justify-between">
                <span class="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Mapeamento {{ idx + 1 }}
                </span>
                <button
                  type="button"
                  class="p-1 text-slate-400 hover:text-red-500"
                  @click="removeMapping(idx)"
                >
                  <span class="i-lucide-trash-2 w-3 h-3" />
                </button>
              </div>

              <div class="min-w-0">
                <div class="flex items-center justify-between gap-2 mb-1">
                  <label class="text-[11px] text-slate-500">Campo da resposta (API)</label>
                  <TemplatePickersToolbar
                    :agent-bot-id="agentBotId"
                    :exclude-slot-id="excludeSlotId"
                    :all-slots="allSlots"
                    :api-sample="apiSample"
                    label="API"
                    @pick="updateMapping(idx, { api_path: ($event.token || '').replace(/^\{\{api\.|\}\}$/g, '').replace(/^\{\{|\}\}$/g, '') })"
                  />
                </div>
                <input
                  :value="mapping.api_path"
                  placeholder="data.user.name"
                  class="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 font-mono"
                  @input="updateMapping(idx, { api_path: $event.target.value })"
                />
              </div>

              <div class="flex justify-center text-slate-400">
                <span class="i-lucide-arrow-down w-3 h-3" />
              </div>

              <div class="min-w-0">
                <div class="flex items-center justify-between gap-2 mb-1">
                  <label class="text-[11px] text-slate-500">Field da conversa</label>
                  <TemplatePickersToolbar
                    :agent-bot-id="agentBotId"
                    :exclude-slot-id="excludeSlotId"
                    :all-slots="allSlots"
                    :api-sample="apiSample"
                    label="Field"
                    hide-api-tab
                    @pick="updateMapping(idx, { field_name: ($event.token || '').replace(/^\{\{field\.|\}\}$/g, '') })"
                  />
                </div>
                <input
                  :value="mapping.field_name"
                  placeholder="nome_cliente"
                  class="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 font-mono"
                  @input="updateMapping(idx, { field_name: $event.target.value })"
                />
              </div>
            </div>
          </div>
          <button
            type="button"
            class="mt-2 inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:bg-slate-50"
            @click="addMapping"
          >
            <span class="i-lucide-plus w-3 h-3" /> Mapeamento
          </button>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
      <button
        type="button"
        class="px-3 py-1.5 text-xs font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:bg-slate-50"
        @click="test"
      >
        <span class="i-lucide-zap w-3 h-3 mr-1" />
        {{ $t('OMNI.SLOTS.API_AUTOMATIONS.TEST', 'Testar condições') }}
      </button>
      <button
        type="button"
        class="px-3 py-1.5 text-xs font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:bg-slate-50"
        @click="cancel"
      >
        {{ $t('OMNI.COMMON.CANCEL', 'Cancelar') }}
      </button>
      <button
        type="button"
        class="px-3 py-1.5 text-xs font-medium rounded bg-woot-500 text-white hover:bg-woot-600"
        @click="submit"
      >
        {{ $t('OMNI.COMMON.SAVE', 'Salvar') }}
      </button>
    </div>
  </div>
</template>
